import { proxy, subscribe, snapshot } from 'valtio'
import posthogClient from 'posthog-js'
import { buildBackendUrl } from '@/lib/backend-url'

export const AVAILABLE_STATUSES = ['todo', 'done']
// Status is just a string, no need for a redundant alias

// Initial State
const initialState = {
  todos: [
    {
      id: '1',
      listId: 'personal',
      text: 'Buy groceries',
      description: 'Get milk, eggs, and bread',
      completed: false,
      pinned: true,
      startDate: new Date().toISOString(),
      priority: 'medium',
      status: 'todo',
      subtasks: [],
      locations: [
        { id: '1-loc1', name: 'Whole Foods Market', lat: 40.7128, lng: -74.006 },
        { id: '1-loc2', name: 'Local Bakery', lat: 40.72, lng: -73.99 },
      ],
      attachments: [],
      reactions: {
        '🛒': ['user1', 'user2'],
        '🥛': ['me'],
      },
      isExpanded: false,
      comments: [],
    },
    {
      id: '2',
      listId: 'personal',
      text: 'Walk the dog',
      completed: true,
      priority: 'low',
      pinned: false,
      status: 'done',
      subtasks: [],
      timeSpent: 0,
      reactions: {
        '🐕': ['user3'],
        '✨': ['me', 'user4'],
      },
      isExpanded: false,
      comments: [],
    },
  ],
  lists: [
    {
      id: 'personal',
      name: 'Personal',
      color: '#3b82f6',
    },
    {
      id: 'work',
      name: 'Work',
      color: '#ef4444',
    },
  ],
  sectionColors: {
    todo: '#64748b',
    // slate-500
    done: '#22c55e', // green-500
  },
  uiSettings: {
    showTime: true,
    showWeather: true,
    showSidebar: true,
    showRightPanel: true,
    showStats: true,
    showSearch: true,
    showViewFilter: true,
    showCommandCenter: true,
    showShortcuts: true,
    showTheme: true,
    hiddenSections: [],
    enabledFeatures: {
      subtasks: true,
      reminders: true,
      attachments: true,
      location: true,
      assignee: true,
      reactions: true,
      comments: true,
      dates: true,
    },
    sidebarWidth: 280,
    rightSidebarWidth: 400,
    isContextMenuOpen: false,
  },
  aiActions: 0,
  attachments: [],
  teammates: [
    {
      id: 'user-1',
      name: 'Kawzaki Honda',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
    },
    {
      id: 'user-2',
      name: 'Sarah Wilson',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
    },
    {
      id: 'user-3',
      name: 'James Chen',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
    },
    {
      id: 'user-4',
      name: 'Elena Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
    },
  ],
  latestEventId: 0,
  pendingMutations: [],
  isLoading: true,
}

// Load from local storage
const loadState = () => {
  try {
    const stored = localStorage.getItem('todo-storage')
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...initialState,
        ...parsed,
        isLoading: true, // Always start as loading when sync is active
        // Ensure all loaded todos update their expanded state to false to avoid UI sync issues
        todos: (parsed.todos || initialState.todos).map((t) => ({ ...t, isExpanded: false })),
        // Explicitly ensure lists and sectionColors exist as they might be missing in older storage
        lists: parsed.lists || initialState.lists,
        sectionColors: parsed.sectionColors || initialState.sectionColors,
        uiSettings: {
          ...initialState.uiSettings,
          ...(parsed.uiSettings || {}),
          enabledFeatures: {
            ...initialState.uiSettings.enabledFeatures,
            ...(parsed.uiSettings?.enabledFeatures || {}),
          },
          sidebarWidth: parsed.uiSettings?.sidebarWidth ?? initialState.uiSettings.sidebarWidth,
          rightSidebarWidth:
            parsed.uiSettings?.rightSidebarWidth ?? initialState.uiSettings.rightSidebarWidth,
        },
        latestEventId: parsed.latestEventId || 0,
        pendingMutations: parsed.pendingMutations || [],
        attachments: parsed.attachments || [],
        teammates: parsed.teammates || initialState.teammates,
      }
    }
  } catch (e) {
    console.error('Failed to load state', e)
  }
  return initialState
}
export const store = proxy(loadState())

// --- Realtime Sync ---
import { getSocket } from './lib/party'

const authToken = localStorage.getItem('zoy_auth_token') || ''
const projectId = localStorage.getItem('zoy_project_id') || 'home'
const socket = getSocket(projectId, authToken)
let isSocketAuthenticated = false

const toTodoFromTaskRow = (t, reminders = [], locations = []) => {
  const taskReminders = reminders
    ? reminders
        .filter((r) => r.task_id === t.id)
        .map((r) => ({
          remindAt: r.remind_at,
          method: r.method,
        }))
    : []

  const taskLocations = locations
    ? locations
        .filter((l) => l.task_id === t.id)
        .map((l) => ({
          id: l.id,
          name: l.name,
          lat: l.latitude,
          lng: l.longitude,
          address: l.address,
        }))
    : []

  return {
    id: t.id,
    listId: t.list_id || 'personal',
    text: t.title || '',
    description: t.description || '',
    completed: !!t.completed_at,
    status: t.status || 'todo',
    priority: t.priority || 'medium',
    isExpanded: false,
    subtasks: [],
    comments: [],
    startDate: t.start_at || t.due_at || new Date().toISOString(),
    endDate: t.due_at || null,
    reactions: {},
    assignees: [],
    reminders: taskReminders,
    locations: taskLocations,
    pinned: false,
    timeSpent: 0,
  }
}

const upsertTodoById = (nextTodo) => {
  if (!nextTodo?.id) return
  const existingIndex = store.todos.findIndex((t) => t.id === nextTodo.id)
  if (existingIndex === -1) {
    store.todos.push(proxy(nextTodo))
    return
  }
  const existingTodo = store.todos[existingIndex]
  // Mutate existing proxy to preserve reactivity
  Object.assign(existingTodo, nextTodo)
}

const dedupeTodosById = () => {
  const seen = new Set()
  const uniqueTodos = []
  let hasDuplicates = false
  for (const todo of store.todos) {
    if (todo?.id && !seen.has(todo.id)) {
      seen.add(todo.id)
      uniqueTodos.push(todo)
    } else {
      hasDuplicates = true
    }
  }
  if (hasDuplicates) {
    store.todos = uniqueTodos
  }
}

if (socket) {
  // Safety timeout: if we don't get a snapshot within 2s, stop showing loading
  setTimeout(() => {
    if (store.isLoading) {
      console.warn('Loading timeout reached, forcing isLoading = false')
      store.isLoading = false
    }
  }, 2000)

  socket.addEventListener('open', () => {
    isSocketAuthenticated = false
    console.log('Socket opened, sending hello')
    // Send hello with sinceEventId
    socket.send(
      JSON.stringify({
        type: 'hello',
        token: authToken,
        projectId,
        sinceEventId: store.latestEventId,
      }),
    )
  })

  socket.addEventListener('message', (event) => {
    const msg = JSON.parse(event.data)
    if (msg.type === 'hello_ack') {
      isSocketAuthenticated = true
      console.log('Hello acknowledged, replaying pending mutations:', store.pendingMutations.length)
      store.pendingMutations.forEach((m) => {
        socket.send(JSON.stringify(m))
      })
      // If we are up to date and no snapshot coming, we can stop loading
      if (store.latestEventId > 0) {
        store.isLoading = false
      }
    } else if (msg.type === 'snapshot') {
      store.isLoading = false
      if (typeof msg.latestEventId === 'number' && msg.latestEventId < store.latestEventId) {
        console.warn('Ignoring stale snapshot', {
          snapshotEventId: msg.latestEventId,
          localEventId: store.latestEventId,
        })
        return
      }
      const { tasks, lists, comments, reminders, locations, attachments } = msg.data
      store.latestEventId = msg.latestEventId

      console.log('Snapshot received', { latestEventId: msg.latestEventId, tasks: tasks?.length })

      if (attachments) {
        store.attachments = attachments.map((a) => ({
          id: a.id,
          task_id: a.task_id,
          file_name: a.file_name,
          file_size: a.file_size,
          mime_type: a.mime_type,
          s3_key: a.s3_key,
        }))
      }

      if (tasks) {
        const serverTaskIds = new Set(tasks.map((t) => t.id))
        const localById = new Map(store.todos.map((todo) => [todo.id, todo]))

        // Update or add tasks from server
        tasks.forEach((t) => {
          const serverTodo = toTodoFromTaskRow(t, reminders, locations)
          const localTodo = localById.get(serverTodo.id)
          if (localTodo) {
            // Merge server data into local proxy, but keep local expanded state
            const wasExpanded = localTodo.isExpanded
            Object.assign(localTodo, serverTodo)
            localTodo.isExpanded = wasExpanded
          } else {
            store.todos.push(proxy(serverTodo))
          }
        })

        // Handle tasks that are in local state but NOT in server snapshot
        const optimisticCreateIds = new Set(
          store.pendingMutations
            .filter((m) => m?.name === 'task.create' && m?.args?.id)
            .map((m) => m.args.id),
        )

        // Remove tasks that are not in server snapshot AND not pending creation
        // (meaning they were deleted on server or moved out of this project/list)
        const filteredTodos = store.todos.filter(
          (todo) => serverTaskIds.has(todo.id) || optimisticCreateIds.has(todo.id),
        )

        if (filteredTodos.length !== store.todos.length) {
          store.todos = filteredTodos
        }

        dedupeTodosById()
      }
      if (lists && lists.length > 0) {
        store.lists = lists.map((l) => ({
          id: l.id,
          name: l.name,
          color: '#3b82f6',
        }))
      }
    } else if (msg.type === 'patch') {
      store.isLoading = false
      if (typeof msg.latestEventId === 'number' && msg.latestEventId < store.latestEventId) {
        console.warn('Ignoring stale patch', {
          patchEventId: msg.latestEventId,
          localEventId: store.latestEventId,
        })
        return
      }
      console.log('Patch received', msg.events)
      store.latestEventId = msg.latestEventId

      msg.events.forEach((e) => {
        if (e.entity_type === 'list') {
          const payload = JSON.parse(e.payload_json || '{}')
          if (e.action === 'list.create') {
            if (!store.lists.some((list) => list.id === e.entity_id)) {
              store.lists.push({
                id: e.entity_id,
                name: payload.name || 'Untitled List',
                color: '#3b82f6',
              })
            }
          } else if (e.action === 'list.update') {
            const list = store.lists.find((item) => item.id === e.entity_id)
            if (list && typeof payload.name === 'string' && payload.name.trim()) {
              list.name = payload.name.trim()
            }
          }
        } else if (e.entity_type === 'task') {
          const payload = JSON.parse(e.payload_json)
          if (e.action === 'task.create') {
            const title = (payload.title || '').trim()
            if (!title) return // Ignore empty tasks from sync

            const newTask = {
              id: payload.id || e.entity_id,
              listId: payload.listId || 'personal',
              text: title,
              description: payload.description || '',
              completed: false,
              status: 'todo',
              priority: payload.priority || 'medium',
              isExpanded: false,
              subtasks: [],
              comments: [],
              startDate: payload.startAt || payload.dueAt || new Date().toISOString(),
              endDate: payload.dueAt || null,
              reactions: {},
              assignees: [],
              reminders: [],
              locations: [],
            }
            upsertTodoById(newTask)
          } else if (e.action === 'task.update') {
            const taskIndex = store.todos.findIndex((t) => t.id === e.entity_id)
            if (taskIndex !== -1) {
              const task = store.todos[taskIndex]
              if (payload.title !== undefined) {
                const trimmedTitle = payload.title.trim()
                if (trimmedTitle) task.text = trimmedTitle
              }
              if (payload.description !== undefined) task.description = payload.description
              if (payload.status !== undefined) task.status = payload.status
              if (payload.priority !== undefined) task.priority = payload.priority
              if (payload.completed_at !== undefined) task.completed = !!payload.completed_at
              if (payload.start_at !== undefined) task.startDate = payload.start_at
              if (payload.due_at !== undefined) task.endDate = payload.due_at
              if (payload.list_id !== undefined) task.listId = payload.list_id
            }
          } else if (e.action === 'task.delete') {
            const index = store.todos.findIndex((t) => t.id === e.entity_id)
            if (index !== -1) {
              store.todos.splice(index, 1)
            }
          }
        } else if (e.entity_type === 'reminder') {
          const payload = JSON.parse(e.payload_json)
          if (e.action === 'reminder.create') {
            const task = store.todos.find((t) => t.id === payload.taskId)
            if (task) {
              if (!task.reminders) task.reminders = []
              const exists = task.reminders.some(
                (r) => r.remindAt === payload.remindAt && r.method === payload.method,
              )
              if (!exists) {
                task.reminders.push({
                  remindAt: payload.remindAt,
                  method: payload.method,
                })
              }
            }
          } else if (e.action === 'reminder.due') {
            // You could trigger a local notification or sound here if you want
            console.log('Reminder due for task:', payload.taskId)
          }
        } else if (e.entity_type === 'location') {
          const payload = JSON.parse(e.payload_json)
          if (e.action === 'task.location.add') {
            const task = store.todos.find((t) => t.id === payload.taskId)
            if (task) {
              if (!task.locations) task.locations = []
              if (!task.locations.some((l) => l.id === payload.id)) {
                task.locations.push({
                  id: payload.id,
                  name: payload.name,
                  lat: payload.lat,
                  lng: payload.lng,
                  address: payload.address,
                })
              }
            }
          } else if (e.action === 'task.location.remove') {
            const task = store.todos.find((t) => t.locations?.some((l) => l.id === e.entity_id))
            if (task) {
              task.locations = task.locations.filter((l) => l.id !== e.entity_id)
            }
          }
        } else if (e.entity_type === 'attachment') {
          const payload = JSON.parse(e.payload_json)
          if (e.action === 'task.attachment.add') {
            if (!store.attachments.some((a) => a.id === payload.id)) {
              store.attachments.push({
                id: payload.id,
                task_id: payload.taskId,
                file_name: payload.fileName,
                file_size: payload.fileSize,
                mime_type: payload.mimeType,
                s3_key: payload.s3Key,
              })
            }
          } else if (e.action === 'task.attachment.remove') {
            store.attachments = store.attachments.filter((a) => a.id !== e.entity_id)
          }
        }
      })
      dedupeTodosById()
    } else if (msg.type === 'mutation_ack') {
      // Remove from pending mutations
      store.pendingMutations = store.pendingMutations.filter((m) => m.requestId !== msg.requestId)
      console.log(
        'Mutation acknowledged:',
        msg.requestId,
        'Remaining pending:',
        store.pendingMutations.length,
      )
    }
  })
}

// Persistence - debounced to avoid conflicts during rapid updates
let persistenceTimeout = null
subscribe(store, () => {
  if (persistenceTimeout) clearTimeout(persistenceTimeout)
  persistenceTimeout = setTimeout(() => {
    localStorage.setItem('todo-storage', JSON.stringify(store))
  }, 500) // Increased debounce to 500ms
})

// --- Robust History Management ---

// Export historyStore for UI observation
export const historyStore = proxy({
  past: [],
  future: [],
})
const historyState = historyStore

// We need to keep track of "last valid state" to push to past *before* new changes.
// But `subscribe` gives us the state *after* change.
// So we must initialize `lastSnapshot` with initial state.

let lastSnapshotJson = JSON.stringify(snapshot(store))
let isHistoryOperation = false
let historyTimeout = null

subscribe(store, () => {
  if (isHistoryOperation) return

  if (historyTimeout) clearTimeout(historyTimeout)
  historyTimeout = setTimeout(() => {
    const currentSnapshotJson = JSON.stringify(snapshot(store))

    // Only save if different
    if (currentSnapshotJson !== lastSnapshotJson) {
      // Limit history size to 50 items to prevent memory bloat
      if (historyState.past.length >= 50) {
        historyState.past.shift()
      }
      historyState.past.push(lastSnapshotJson)
      historyState.future = [] // Clear future on new branch
      lastSnapshotJson = currentSnapshotJson
    }
  }, 1000) // Debounce history snapshotting as it is very expensive
})
export const undo = () => {
  if (historyState.past.length === 0) return false
  isHistoryOperation = true
  try {
    const previousJson = historyState.past.pop()
    if (previousJson) {
      // Save current to future
      const currentJson = JSON.stringify(snapshot(store))
      historyState.future.unshift(currentJson)

      // Apply previous
      const previousState = JSON.parse(previousJson)
      store.todos = previousState.todos
      store.lists = previousState.lists || initialState.lists

      // Sync our tracker
      lastSnapshotJson = previousJson
      posthogClient.capture('undo_performed')
      window.gtag('event', 'undo_performed')
      return true
    }
  } finally {
    // Use timeout to ensure we don't capture the undo itself if subscribe fires async (usually sync though)
    // But safe to just reset flag.
    isHistoryOperation = false
  }
  return false
}
export const redo = () => {
  if (historyState.future.length === 0) return false
  isHistoryOperation = true
  try {
    const nextJson = historyState.future.shift()
    if (nextJson) {
      // Save current to past
      const currentJson = JSON.stringify(snapshot(store))
      historyState.past.push(currentJson)

      // Apply next
      const nextState = JSON.parse(nextJson)
      store.todos = nextState.todos
      store.lists = nextState.lists || initialState.lists

      // Sync our tracker
      lastSnapshotJson = nextJson
      posthogClient.capture('redo_performed')
      window.gtag('event', 'redo_performed')
      return true
    }
  } finally {
    isHistoryOperation = false
  }
  return false
}

// Helper for creating consistent task objects
export const createTodo = (overrides = {}) => {
  return {
    id: crypto.randomUUID(),
    listId: 'personal',
    text: overrides.text?.trim() || 'New Task',
    description: '',
    completed: overrides.status === 'done',
    priority: 'medium',
    status: 'todo',
    subtasks: [],
    locations: [],
    assignees: [],
    timeSpent: 0,
    startDate: new Date().toISOString(),
    reactions: {},
    pinned: false,
    isExpanded: false,
    comments: [],
    ...overrides,
  }
}

// Cleanup existing empty tasks on startup
if (store.todos) {
  store.todos = store.todos.filter((t) => t.text && t.text.trim().length > 0)
}
export const createList = (name, overrides = {}) => ({
  id: crypto.randomUUID(),
  name,
  pinned: false,
  color:
    overrides.color ||
    `#${(crypto.getRandomValues(new Uint32Array(1))[0] & 0xffffff).toString(16).padStart(6, '0')}`,
  ...overrides,
})
export const createSubtask = (text) => ({
  id: crypto.randomUUID(),
  text,
  completed: false,
  completedAt: null,
})
export const createComment = (text) => ({
  id: crypto.randomUUID(),
  userId: 'me',
  userName: 'Me',
  text,
  timestamp: new Date().toISOString(),
})

// Export actions for cleaner usage, but direct store mutation works too.
export const actions = {
  addTodo: (todo) => {
    // Prevent adding empty tasks
    const trimmedText = (todo.text || '').trim()
    if (!trimmedText) {
      console.warn('Attempted to add empty todo')
      return
    }
    todo.text = trimmedText

    // FIXED: Check for duplicates before adding
    const existingTodo = store.todos.find((t) => t.id === todo.id)
    if (existingTodo) {
      console.warn('Attempted to add duplicate todo:', todo.id)
      return
    }

    store.todos.push(todo)
    posthogClient.capture('todo_added', { priority: todo.priority, list_id: todo.listId })
    window.gtag('event', 'todo_added')

    // Relay to Socket
    const mutation = {
      type: 'mutation',
      requestId: crypto.randomUUID(),
      name: 'task.create',
      args: {
        id: todo.id,
        title: todo.text,
        listId: todo.listId,
        sortKey: 'a0', // TODO: calc sort key
        priority: todo.priority,
        description: todo.description || '',
        startAt: todo.startDate,
        dueAt: todo.endDate,
        locationCount: todo.locations?.length || 0,
        locations: todo.locations?.map((l) => ({
          id: l.id,
          name: l.name,
          lat: l.lat,
          lng: l.lng,
          address: l.address,
        })),
        assignees: todo.assignees || [],
        reminders: todo.reminders || [],
      },
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }
  },
  updateTodo: (id, updates) => {
    const index = store.todos.findIndex((t) => t.id === id)
    if (index === -1) {
      console.warn('Attempted to update non-existent todo:', id)
      return
    }

    const currentTodo = store.todos[index]

    const normalizedUpdates = { ...updates }

    // Prevent updating to empty text
    if (normalizedUpdates.text !== undefined) {
      const trimmedText = normalizedUpdates.text.trim()
      if (!trimmedText) {
        console.warn('Attempted to update todo with empty text')
        return
      }
      normalizedUpdates.text = trimmedText
    }

    if (normalizedUpdates.status !== undefined && normalizedUpdates.completed === undefined) {
      normalizedUpdates.completed = normalizedUpdates.status === 'done'
    }
    if (normalizedUpdates.completed !== undefined && normalizedUpdates.status === undefined) {
      if (normalizedUpdates.completed) {
        normalizedUpdates.status = 'done'
      } else if (currentTodo.status === 'done') {
        normalizedUpdates.status = 'todo'
      }
    }

    // Relay to Socket
    const payload = { id }
    if (normalizedUpdates.text !== undefined) payload.title = normalizedUpdates.text
    if (normalizedUpdates.description !== undefined)
      payload.description = normalizedUpdates.description
    if (normalizedUpdates.status !== undefined) payload.status = normalizedUpdates.status
    if (normalizedUpdates.priority !== undefined) payload.priority = normalizedUpdates.priority
    if (normalizedUpdates.startDate !== undefined) payload.start_at = normalizedUpdates.startDate
    if (normalizedUpdates.endDate !== undefined) payload.due_at = normalizedUpdates.endDate
    if (normalizedUpdates.listId !== undefined) payload.list_id = normalizedUpdates.listId
    if (normalizedUpdates.completed !== undefined)
      payload.completed_at = normalizedUpdates.completed ? Date.now() : null

    const mutation = {
      type: 'mutation',
      requestId: crypto.randomUUID(),
      name: 'task.update',
      args: payload,
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }

    // FIXED: Handle expansion state first to prevent race conditions
    if (updates.isExpanded === true) {
      store.uiSettings.showRightPanel = true
      // Collapse all other todos
      store.todos.forEach((t) => {
        if (t.id !== id && t.isExpanded) {
          t.isExpanded = false
        }
      })
    }

    const isStatusChange =
      normalizedUpdates.status !== undefined && normalizedUpdates.status !== currentTodo.status
    if (isStatusChange) {
      // FIXED: Update in place first, then reorder to prevent data loss
      // Apply all updates to the current todo
      for (const key in normalizedUpdates) {
        if (normalizedUpdates.hasOwnProperty(key)) {
          currentTodo[key] = normalizedUpdates[key]
        }
      }

      // Force collapse when marking as done
      if (normalizedUpdates.status === 'done') {
        currentTodo.isExpanded = false
      }

      // Reorder: move to appropriate position
      const [movedTodo] = store.todos.splice(index, 1)
      if (movedTodo.status === 'done') {
        store.todos.unshift(movedTodo)
      } else {
        store.todos.push(movedTodo)
      }
    } else {
      // FIXED: Use direct assignment instead of Object.assign for better reactivity
      for (const key in normalizedUpdates) {
        if (normalizedUpdates.hasOwnProperty(key)) {
          currentTodo[key] = normalizedUpdates[key]
        }
      }
    }
  },
  deleteTodo: (id) => {
    const index = store.todos.findIndex((t) => t.id === id)
    if (index !== -1) {
      store.todos.splice(index, 1)
      window.record('todo_deleted')

      const mutation = {
        type: 'mutation',
        requestId: crypto.randomUUID(),
        name: 'task.delete',
        args: { id },
      }
      store.pendingMutations.push(mutation)
      if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
        socket.send(JSON.stringify(mutation))
      }
    }
  },
  setTodos: (todos) => {
    store.todos = todos
  },
  reorderTodo: (activeId, overId) => {
    const oldIndex = store.todos.findIndex((t) => t.id === activeId)
    const newIndex = store.todos.findIndex((t) => t.id === overId)
    if (oldIndex !== -1 && newIndex !== -1) {
      // Move the item
      const [movedItem] = store.todos.splice(oldIndex, 1)
      store.todos.splice(newIndex, 0, movedItem)
    }
  },
  addList: (list) => {
    store.lists.push(list)
    window.record('list_added')

    const mutation = {
      type: 'mutation',
      requestId: crypto.randomUUID(),
      name: 'list.create',
      args: {
        id: list.id,
        name: list.name,
        sortKey: 'a0',
      },
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }
  },
  updateList: (id, updates) => {
    const index = store.lists.findIndex((l) => l.id === id)
    if (index !== -1) {
      Object.assign(store.lists[index], updates)

      const mutationArgs = { id }
      if (typeof updates.name === 'string' && updates.name.trim()) {
        mutationArgs.name = updates.name.trim()
      }

      if (mutationArgs.name) {
        const mutation = {
          type: 'mutation',
          requestId: crypto.randomUUID(),
          name: 'list.update',
          args: mutationArgs,
        }
        store.pendingMutations.push(mutation)
        if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
          socket.send(JSON.stringify(mutation))
        }
      }
    }
  },
  insertList: (list, index) => {
    store.lists.splice(index, 0, list)
  },
  reorderList: (activeId, overId) => {
    const oldIndex = store.lists.findIndex((list) => list.id === activeId)
    const newIndex = store.lists.findIndex((list) => list.id === overId)
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return
    }

    const [movedList] = store.lists.splice(oldIndex, 1)
    store.lists.splice(newIndex, 0, movedList)
  },
  deleteList: (id) => {
    const index = store.lists.findIndex((l) => l.id === id)
    if (index !== -1) {
      store.lists.splice(index, 1)
      // Cleanup: move items from deleted list to first available list or delete them
      const firstList = store.lists[0]
      if (store.lists.length > 0) {
        store.todos.forEach((todo) => {
          if (todo.listId === id) {
            todo.listId = firstList.id
          }
        })
      } else {
        store.todos = store.todos.filter((t) => t.listId !== id)
      }
    }
  },
  reorderTodos: (activeId, overId, selectedIds) => {
    const activeIndex = store.todos.findIndex((t) => t.id === activeId)
    const overIndex = store.todos.findIndex((t) => t.id === overId)
    if (activeIndex === -1 || overIndex === -1) return

    // 1. Identify all items to move
    // If the active item is in the selection, move all selected items.
    // If not, it shouldn't be triggering a multi-drag (but we handle it safely).
    const itemsToMoveIds = selectedIds.has(activeId) ? Array.from(selectedIds) : [activeId]

    // 2. Extract items to move and separate them from the list
    // We need to maintain their relative order
    const currentTodos = [...store.todos]
    const itemsToMove = []
    const remainingTodos = []
    currentTodos.forEach((todo) => {
      if (itemsToMoveIds.includes(todo.id)) {
        itemsToMove.push(todo)
      } else {
        remainingTodos.push(todo)
      }
    })

    // Restore/Ensure relative order of selected items based on original positions
    // (The loop above already preserves relative order of items as they appeared in currentTodos)

    // 3. Find insertion index
    // The overId is now in remainingTodos (unless it was part of selection which implies self-drop or weird state)
    // If overId was part of selection, dragging into itself -> no-op usually, or strictly reorder within selection?
    // Dragging a selection ONTO a selection typically means "place this selection relative to that item".
    // If overId is in the moved set, we need to find where it *would have been* or *is*?
    // Actually, dnd-kit `over` is usually the item UNDER the cursor.
    // If we dragged a group, and `over` is one of the dragged items, it's a self-move which is usually void.
    if (itemsToMoveIds.includes(overId)) {
      return
    }
    const newOverIndex = remainingTodos.findIndex((t) => t.id === overId)
    if (newOverIndex === -1) return // Should not happen if overId not in selection

    // Determine if we are moving "down" or "up" relative to the whole list can be catchy with multiple items.
    // A simple heuristic for standard lists:
    // If the dragged item (activeId) was BEFORE the drop target (overId) in the original list,
    // we generally want to place it AFTER the drop target.
    // If it was AFTER, we place it BEFORE.

    // Let's use the active item's original index vs over item's original index
    const isMovingDown = activeIndex < overIndex
    const insertIndex = isMovingDown ? newOverIndex + 1 : newOverIndex

    // 4. Insert
    remainingTodos.splice(insertIndex, 0, ...itemsToMove)
    store.todos = remainingTodos
  },
  updateSectionColor: (status, color) => {
    if (status === 'todo') {
      store.sectionColors.todo = color
    } else if (status === 'done') {
      store.sectionColors.done = color
    }
  },
  insertTodoAfter: (precedingTaskId, todo) => {
    const index = store.todos.findIndex((t) => t.id === precedingTaskId)
    if (index !== -1) {
      store.todos.splice(index + 1, 0, todo)
    } else {
      store.todos.push(todo)
    }

    // Relay to Socket
    const mutation = {
      type: 'mutation',
      requestId: crypto.randomUUID(),
      name: 'task.create',
      args: {
        id: todo.id,
        title: todo.text,
        listId: todo.listId,
        sortKey: 'a0', // TODO: calc proper sort key
        priority: todo.priority,
        description: todo.description || '',
        startAt: todo.startDate,
        dueAt: todo.endDate,
        locations: todo.locations,
        assignees: todo.assignees,
      },
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }
  },
  insertTodoBefore: (followingTaskId, todo) => {
    const index = store.todos.findIndex((t) => t.id === followingTaskId)
    if (index !== -1) {
      store.todos.splice(index, 0, todo)
    } else {
      store.todos.unshift(todo)
    }

    // Relay to Socket
    const mutation = {
      type: 'mutation',
      requestId: crypto.randomUUID(),
      name: 'task.create',
      args: {
        id: todo.id,
        title: todo.text,
        listId: todo.listId,
        sortKey: 'a0', // TODO: calc proper sort key
        priority: todo.priority,
        description: todo.description || '',
        startAt: todo.startDate,
        dueAt: todo.endDate,
        locations: todo.locations,
        assignees: todo.assignees,
      },
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }
  },
  insertTodoAt: (index, todo) => {
    store.todos.splice(index, 0, todo)

    // Relay to Socket
    const mutation = {
      type: 'mutation',
      requestId: crypto.randomUUID(),
      name: 'task.create',
      args: {
        id: todo.id,
        title: todo.text,
        listId: todo.listId,
        sortKey: 'a0', // TODO: calc proper sort key
        priority: todo.priority,
        description: todo.description || '',
        startAt: todo.startDate,
        dueAt: todo.endDate,
        locations: todo.locations,
        assignees: todo.assignees,
      },
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }
  },
  setAllExpanded: (expanded) => {
    // Only support collapsing all to maintain the "only one expanded" rule
    store.todos.forEach((t) => {
      t.isExpanded = false
    })
  },
  markAllAsDone: () => {
    store.todos.forEach((t) => {
      if (t.status === 'todo') {
        t.completed = true
        t.status = 'done'
        t.isExpanded = false
      }
    })
    // Move all 'done' todos to the top to maintain consistency with logic in updateTodo
    const doneTodos = store.todos.filter((t) => t.status === 'done')
    const otherTodos = store.todos.filter((t) => t.status !== 'done')
    store.todos = [...doneTodos, ...otherTodos]
  },
  markAllAsTodo: () => {
    store.todos.forEach((t) => {
      if (t.status === 'done') {
        t.completed = false
        t.status = 'todo'
      }
    })
    const todoTodos = store.todos.filter((t) => t.status === 'todo')
    const otherTodos = store.todos.filter((t) => t.status !== 'todo')
    store.todos = [...todoTodos, ...otherTodos]
  },
  toggleUIVisibility: (key) => {
    const ui = store.uiSettings
    switch (key) {
      case 'showTime':
        ui.showTime = !ui.showTime
        break
      case 'showWeather':
        ui.showWeather = !ui.showWeather
        break
      case 'showSidebar':
        ui.showSidebar = !ui.showSidebar
        break
      case 'showRightPanel':
        ui.showRightPanel = !ui.showRightPanel
        break
      case 'showStats':
        ui.showStats = !ui.showStats
        break
      case 'showSearch':
        ui.showSearch = !ui.showSearch
        break
      case 'showViewFilter':
        ui.showViewFilter = !ui.showViewFilter
        break
      case 'showCommandCenter':
        ui.showCommandCenter = !ui.showCommandCenter
        break
      case 'showShortcuts':
        ui.showShortcuts = !ui.showShortcuts
        break
      case 'showTheme':
        ui.showTheme = !ui.showTheme
        break
      default:
        break
    }
  },
  setUIVisibility: (key, value) => {
    const ui = store.uiSettings
    switch (key) {
      case 'showTime':
        ui.showTime = value
        break
      case 'showWeather':
        ui.showWeather = value
        break
      case 'showSidebar':
        ui.showSidebar = value
        break
      case 'showRightPanel':
        ui.showRightPanel = value
        break
      case 'showStats':
        ui.showStats = value
        break
      case 'showSearch':
        ui.showSearch = value
        break
      case 'showViewFilter':
        ui.showViewFilter = value
        break
      case 'showCommandCenter':
        ui.showCommandCenter = value
        break
      case 'showShortcuts':
        ui.showShortcuts = value
        break
      case 'showTheme':
        ui.showTheme = value
        break
      default:
        break
    }
  },
  toggleSectionVisibility: (status) => {
    const index = store.uiSettings.hiddenSections.indexOf(status)
    if (index === -1) {
      store.uiSettings.hiddenSections.push(status)
    } else {
      store.uiSettings.hiddenSections.splice(index, 1)
    }
  },
  toggleTaskFeature: (feature) => {
    const features = store.uiSettings.enabledFeatures
    switch (feature) {
      case 'subtasks':
        features.subtasks = !features.subtasks
        break
      case 'reminders':
        features.reminders = !features.reminders
        break
      case 'attachments':
        features.attachments = !features.attachments
        break
      case 'location':
        features.location = !features.location
        break
      case 'assignee':
        features.assignee = !features.assignee
        break
      case 'reactions':
        features.reactions = !features.reactions
        break
      case 'comments':
        features.comments = !features.comments
        break
      case 'dates':
        features.dates = !features.dates
        break
      default:
        break
    }
  },
  setSidebarWidth: (width) => {
    store.uiSettings.sidebarWidth = width
  },
  setRightSidebarWidth: (width) => {
    store.uiSettings.rightSidebarWidth = width
  },
  setContextMenuOpen: (open) => {
    store.uiSettings.isContextMenuOpen = open
  },
  duplicateTodo: (id) => {
    const todo = store.todos.find((t) => t.id === id)
    if (todo) {
      // Deep copy to avoid shared references
      const newTodo = createTodo({
        ...todo,
        id: crypto.randomUUID(),
        text: `${todo.text} (Copy)`,
        timeSpent: 0,
        subtasks: (todo.subtasks || []).map((s) => ({
          ...s,
          id: crypto.randomUUID(),
        })),
        assignees: [...(todo.assignees || [])],
        reminders: (todo.reminders || []).map((r) => ({ ...r })),
        locations: (todo.locations || []).map((l) => ({ ...l, id: crypto.randomUUID() })),
        comments: [],
        reactions: {},
        pinned: false,
        isExpanded: false,
      })

      const index = store.todos.indexOf(todo)
      store.todos.splice(index + 1, 0, proxy(newTodo))

      // Relay to Socket
      const mutation = {
        type: 'mutation',
        requestId: crypto.randomUUID(),
        name: 'task.create',
        args: {
          id: newTodo.id,
          title: newTodo.text,
          listId: newTodo.listId,
          sortKey: 'a0', // TODO: calc sort key
          priority: newTodo.priority,
          description: newTodo.description || '',
          startAt: newTodo.startDate,
          dueAt: newTodo.endDate,
          locations: newTodo.locations,
          assignees: newTodo.assignees,
        },
      }
      store.pendingMutations.push(mutation)
      if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
        socket.send(JSON.stringify(mutation))
      }
    }
  },
  attachFiles: async (id, files) => {
    // Real upload
    const results = []
    for (const file of files) {
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await fetch(buildBackendUrl(`/api/tasks/${id}/attachments`), {
          method: 'POST',
          body: formData,
        })

        if (response.ok) {
          const data = await response.json()
          const att = { ...data, task_id: id, s3Key: data.s3Key }
          results.push(att)

          // Broadcast mutation for real-time sync
          const mutation = {
            requestId: crypto.randomUUID(),
            name: 'task.attachment.add',
            args: {
              id: att.id,
              taskId: id,
              fileName: att.fileName,
              fileSize: att.fileSize,
              mimeType: att.mimeType,
              s3Key: att.s3Key,
            },
          }
          store.pendingMutations.push(mutation)
          if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
            socket.send(JSON.stringify(mutation))
          }
        }
      } catch (err) {
        console.error('File upload failed', err)
      }
    }

    if (results.length > 0) {
      // Local optimistic update (although mutation_ack will also handle it)
      // Actually we wait for ack or snapshot usually, but let's push for immediate feel
      store.attachments.push(...results)
    }
    return results
  },
  removeAttachment: (id) => {
    store.attachments = store.attachments.filter((a) => a.id !== id)

    const mutation = {
      requestId: crypto.randomUUID(),
      name: 'task.attachment.remove',
      args: { id },
    }
    store.pendingMutations.push(mutation)
    if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
      socket.send(JSON.stringify(mutation))
    }
  },
  togglePinned: (id) => {
    const todo = store.todos.find((t) => t.id === id)
    if (todo) {
      todo.pinned = !todo.pinned
    }
  },
  addReminder: (taskId, remindAt, method = 'push') => {
    const todo = store.todos.find((t) => t.id === taskId)
    if (todo) {
      if (!todo.reminders) todo.reminders = []
      const newReminder = {
        remindAt: typeof remindAt === 'string' ? new Date(remindAt).getTime() : remindAt,
        method,
      }
      todo.reminders.push(newReminder)

      const mutation = {
        type: 'mutation',
        requestId: crypto.randomUUID(),
        name: 'reminder.create',
        args: {
          id: `rem_${crypto.randomUUID()}`,
          taskId,
          remindAt: newReminder.remindAt,
          method,
        },
      }
      store.pendingMutations.push(mutation)
      if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
        socket.send(JSON.stringify(mutation))
      }
    }
  },
  addLocation: (taskId, location) => {
    const todo = store.todos.find((t) => t.id === taskId)
    if (todo) {
      if (!todo.locations) todo.locations = []
      const newLoc = {
        id: location.id || crypto.randomUUID(),
        name: location.name,
        lat: location.lat,
        lng: location.lng,
        address: location.address,
      }
      todo.locations.push(newLoc)

      const mutation = {
        type: 'mutation',
        requestId: crypto.randomUUID(),
        name: 'task.location.add',
        args: {
          id: newLoc.id,
          taskId,
          lat: newLoc.lat,
          lng: newLoc.lng,
          name: newLoc.name,
          address: newLoc.address,
        },
      }
      store.pendingMutations.push(mutation)
      if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
        socket.send(JSON.stringify(mutation))
      }
    }
  },
  removeLocation: (locationId) => {
    const todo = store.todos.find((t) => t.locations?.some((l) => l.id === locationId))
    if (todo) {
      todo.locations = todo.locations.filter((l) => l.id !== locationId)

      const mutation = {
        type: 'mutation',
        requestId: crypto.randomUUID(),
        name: 'task.location.remove',
        args: { id: locationId },
      }
      store.pendingMutations.push(mutation)
      if (socket && socket.readyState === WebSocket.OPEN && isSocketAuthenticated) {
        socket.send(JSON.stringify(mutation))
      }
    }
  },
  incrementAIAction: () => {
    store.aiActions += 1
  },
}
